import { Injectable, computed, effect, inject } from '@angular/core';
import {
  getBrowserTimezone,
  getDefaultTimezone,
  setActiveTimezone,
} from '@shared/utils/i18n';
import { UserPreferencesService } from './user-preferences.service';

/**
 * Resuelve la zona horaria efectiva del usuario y la propaga a los formateadores de fecha.
 *
 * Precedencia: preferencia explícita del usuario → zona detectada del navegador → default
 * del sistema (`America/Lima`). El backend entrega instantes en UTC; esta zona solo afecta la
 * presentación.
 *
 * Mantiene sincronizado el estado de módulo (`setActiveTimezone`) que usan las funciones puras
 * de formateo (utils, renderers de AG-Grid), además de exponer la zona como signal para la UI.
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {
  private readonly prefs = inject(UserPreferencesService);

  /**
   * Zona horaria efectiva (IANA). El backend ya resuelve la precedencia
   * usuario → organización → default y la entrega en `effectiveTimezone`; mientras las
   * preferencias no han cargado, se usa la del navegador como aproximación.
   */
  readonly timezone = computed<string>(() => {
    const prefs = this.prefs.preferences();
    const effective = prefs?.effectiveTimezone ?? prefs?.timezone;
    if (effective && effective.trim()) return effective;
    return getBrowserTimezone() ?? getDefaultTimezone().value;
  });

  constructor() {
    // Propaga la zona efectiva al estado de módulo que consumen los formateadores puros.
    effect(() => setActiveTimezone(this.timezone()));
  }
}
